import { CalendarDays, Gift, HandHeart, HeartHandshake, MessageSquare, Shield, Sparkles, Swords, Tv, UserRound, Users, Youtube } from "lucide-react";
import PublicSiteLayout, { PUBLIC_LINKS, PublicCard, PublicLinkButton } from "../components/PublicSiteLayout";

export default function Landing() {
  return (
    <PublicSiteLayout>
      <div className="landing-page">
        <section className="landing-hero">
        <div>
          <div className="label-eyebrow mb-3 text-[hsl(var(--currency-event))]">Parker / TheBarracksRat</div>
          <h1 className="landing-title">
            A place to show up.
          </h1>
          <p className="landing-intro">
            The community behind Parker and The Barracks Rat. Streams, games, guides, giveaways, events, and barracks-grade chaos for people who want a place to show up.
          </p>
          <div className="public-actions mt-6">
            <PublicLinkButton href={PUBLIC_LINKS.twitch} primary><Tv className="w-4 h-4" /> Watch Twitch</PublicLinkButton>
            <PublicLinkButton href={PUBLIC_LINKS.kick}><Sparkles className="w-4 h-4" /> Kick</PublicLinkButton>
            <PublicLinkButton href={PUBLIC_LINKS.youtube}><Youtube className="w-4 h-4" /> YouTube</PublicLinkButton>
            <PublicLinkButton href={PUBLIC_LINKS.discord}><MessageSquare className="w-4 h-4" /> Discord</PublicLinkButton>
          </div>
        </div>
        </section>

      <section className="landing-feature">
        <div className="landing-section-heading">
          <div>
            <div className="label-eyebrow mb-2 text-[hsl(var(--currency-event))]">Featured Broadcast</div>
            <h2>Pull up a seat.</h2>
          </div>
          <PublicLinkButton href={PUBLIC_LINKS.twitch}>Open Twitch</PublicLinkButton>
        </div>
        <div className="landing-player aspect-video overflow-hidden bg-black">
          <iframe
            title="The Barracks Rat featured broadcast"
            src="https://player.twitch.tv/?channel=thebarracksrat&parent=thebarracksrat.com&parent=www.thebarracksrat.com&parent=thebarracksrat.pages.dev&muted=true"
            allowFullScreen
            className="w-full h-full"
          />
        </div>
      </section>

      <section className="landing-story">
        <div className="landing-story-heading">
        <div className="label-eyebrow mb-2 text-[hsl(var(--currency-event))]">The Ratterday Saints</div>
        <h2>A community with a little more room at the table.</h2>
        </div>
        <p>
          The Ratterday Saints are the people who show up for the streams, the games, the jokes, the events, and each other. Watch, lurk, raid, join community nights, and come back for whatever nonsense gets promoted to official doctrine.
        </p>
      </section>

      <section className="landing-story landing-about">
        <div className="landing-story-heading">
          <UserRound className="w-5 h-5 text-[hsl(var(--currency-event))] mb-3" strokeWidth={1.5} />
          <div className="label-eyebrow mb-2 text-[hsl(var(--currency-event))]">Meet Parker</div>
          <h2>Built out of a need to stay connected.</h2>
        </div>
        <div className="landing-about-copy">
          <p>Parker is a medically retired Army veteran. After leaving the Army and spending more time isolated at home, streaming became a way to stay connected and meet people.</p>
          <p>TheBarracksRat started as a stream and became the home of The Ratterday Saints.</p>
        </div>
      </section>

      <section className="landing-pathways-section">
        <div className="label-eyebrow mb-2 text-[hsl(var(--currency-event))]">Ways to join in</div>
        <h2>Find your door.</h2>
        <div className="landing-pathways">
          <PublicCard href={PUBLIC_LINKS.twitch} icon={Tv} title="Watch live" body="Streams, games, raids, community nights, and live chaos across Twitch, Kick, and future platforms." />
          <PublicCard href={PUBLIC_LINKS.youtube} icon={Youtube} title="Videos and guides" body="YouTube videos, tutorials, highlights, and long-form content from Parker." />
          <PublicCard href="/rpg" icon={Swords} title="Barracks RPG" body="See available quests, daily runs, event dungeons, party commands, and the loot players can keep." />
          <PublicCard href="/events" icon={Gift} title="Events and giveaways" body="Community nights, giveaways, special runs, and reasons to check back." />
          <PublicCard href={PUBLIC_LINKS.discord} icon={MessageSquare} title="Join Discord" body="The easiest place to meet people, keep up with announcements, and join the running jokes." />
        </div>
      </section>

      <section id="events" className="landing-values-section">
        <div className="label-eyebrow mb-3 text-[hsl(var(--currency-event))]">Community values</div>
        <div className="landing-values">
          <PublicCard icon={HeartHandshake} title="Friendship" body="A place to hang out, be recognized, and feel less alone." />
          <PublicCard icon={Shield} title="Camaraderie" body="Barracks humor, shared stories, and showing up for the people who show up." />
          <PublicCard icon={Swords} title="Gaming" body="Streams, community games, challenges, and live moments people can rally around." />
          <PublicCard icon={CalendarDays} title="Events" body="Community nights, giveaways, special runs, and creator announcements." />
          <PublicCard icon={HandHeart} title="Looking out" body="A community that tries to notice people, include people, and support people." />
          <PublicCard icon={Users} title="Cause support" body="Veteran support and charity support can appear when there is an active campaign or moment." />
        </div>
      </section>

      <section className="landing-story">
        <div>
        <div className="label-eyebrow mb-2 text-[hsl(var(--currency-event))]">Your place in the community</div>
        <h2 className="text-2xl font-semibold tracking-tight">Future profiles should feel like recognition, not paperwork.</h2>
        </div>
        <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed max-w-3xl">
          Future profile tools are about belonging: preferred name, pronouns, community participation, recognition, and one identity across platforms. TheBarracksBunny may power parts of those tools later, but the point is simple: you have a place here, and the site will remember how you show up.
        </p>
      </section>

      <section className="landing-cta">
        <div>
        <div className="label-eyebrow mb-2 text-[hsl(var(--currency-event))]">Watch / Join</div>
        <h2 className="text-2xl font-semibold tracking-tight text-[hsl(var(--foreground))]">Pick a door and come in.</h2>
        </div>
        <div className="public-actions">
          <PublicLinkButton href={PUBLIC_LINKS.twitch} primary><Tv className="w-4 h-4" /> Twitch</PublicLinkButton>
          <PublicLinkButton href={PUBLIC_LINKS.kick}><Sparkles className="w-4 h-4" /> Kick</PublicLinkButton>
          <PublicLinkButton href={PUBLIC_LINKS.youtube}><Youtube className="w-4 h-4" /> YouTube</PublicLinkButton>
          <PublicLinkButton href={PUBLIC_LINKS.discord}><MessageSquare className="w-4 h-4" /> Discord</PublicLinkButton>
        </div>
      </section>
      </div>
    </PublicSiteLayout>
  );
}
